/**
 * @Author: Luana Gomes
 * @Create Time: 2026-01-07 10:49:18
 * @Modified by: Luana Gomes
 * @Modified time: 2026-04-01 14:48:53
 * @Description:
 */

import React, { useEffect, useState, useCallback, useMemo, useContext, useRef } from "react";
import { intl, fetchData, HTTP_STATUS, SSE, Notifications, getToken, LanguageBaseContext } from "dmsbasereact";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Box, useMediaQuery, IconButton, Tooltip, Drawer } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';

import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import HistoryIcon from '@mui/icons-material/History';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import HomeIcon from "@mui/icons-material/Home";
import UserIcon from "@mui/icons-material/Person";
import RoleIcon from "@mui/icons-material/SupervisedUserCircle";
import ProductsIcom from "@mui/icons-material/Archive";
import SystemIcon from "@mui/icons-material/DisplaySettings";
import SettingIcom from "@mui/icons-material/Settings";
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SchoolIcon from '@mui/icons-material/School';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AlarmIcon from '@mui/icons-material/Alarm';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TimelineIcon from '@mui/icons-material/Timeline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import OutboxIcon from '@mui/icons-material/Outbox';
import {
  useDmsInactivityLogout,
  useDmsPasswordExpiration,
  DmsBarMenuButton,
  DmsNotificationsMenuHeader,
  DmsMainHeader,
  DmsMainSidebar,
  DmsMainSidebarMenu,
  useDmsTokenRefresh,
  DmsMainSidebarOption,
  ROUTES,
  useSidebarToggle,
  DmsPermissions
} from "dmsbasereact";

import { APP_ROUTES } from "../../utils/NamedRoutes";
import { Factory, HowToReg, Security, Business as BusinessIcon, Inventory as InventoryIcon, Groups as GroupsIcon, VerifiedUser as VerifiedUserIcon, Article as ArticleIcon, Apartment as ApartmentIcon, Handshake as HandshakeIcon, MedicalServices as MedicalServicesIcon, LocalPharmacy as LocalPharmacyIcon, Cancel as CancelIcon, Download as DownloadIcon } from "@mui/icons-material";
import { GenerateApiKeyModal } from "../../components/GenerateApiKeyModal/GenerateApiKeyModal";
import { LenaLogo } from "../../components/LenaLogo";
import { Permissions } from "../../constants/Permissions";
import { InstitutionCategoryEnum } from "../../constants/InstitutionType";
import { InstitutionStatusEnum as InstitutionOwnStatusEnum } from "../../constants/InstitutionStatus";
import { DmsHelpCenterButton } from "../../components/DmsHelpCenter/DmsHelpCenterButton";
import { DmsUserInformation } from "../../components/DmsUserInformation";
import { ROUTES as ALERT_ROUTES } from "dmsalertsystemreact";
import { TrainingModal } from "../../components/TrainingModal";
import { AlertBanner } from "../../components/AlertBanner";
import { DocumentUploadBanner } from "../../components/DocumentUploadBanner";
import { useInstitutionStatus, InstitutionStatusEnum } from "../../hooks/useInstitutionStatus";
import { useInstitutionAlerts } from "../../hooks/useInstitutionAlerts";
import { useTrainingAlerts } from "../../hooks/useTrainingAlerts";
import { useDeclarationAlerts } from "../../hooks/useDeclarationAlerts";
import { ExportJobsProvider } from "../../services/AsyncExport/ExportJobsContext";

const ACCOUNT_STATUS = {
  PENDING_CFM_VALIDATION: 1,
  PENDING_DOCUMENT_UPLOAD: 2,
  PENDING_APPROVAL: 3, // Legacy status
  PENDING_TRAINING: 4,
  ACTIVE: 5,
  REJECTED: 6,
  SUSPENDED: 7,
  PENDING_APPROVAL_GESTAO_LENA: 8,
  PENDING_APPROVAL_REGULATORIO: 9,
  PENDING_RECYCLING_TRAINING: 10,
};

const SSO_EXEMPT_LOGINS = ['accenture@accenture.com'];
const SSO_EXEMPT_DOMAIN = '@eurofarma.com';

const isSSOExemptUser = (login) => {
  const lower = (login || '').toLowerCase();
  return SSO_EXEMPT_LOGINS.includes(lower) || lower.endsWith(SSO_EXEMPT_DOMAIN);
};

const PasswordExpirationGuard = () => {
  useDmsPasswordExpiration();
  return null;
};

export const Layout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed, handleClick } = useSidebarToggle();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useDmsTokenRefresh();
  useDmsInactivityLogout();

  const currentUserLogin = getToken()?.login;
  const ssoExempt = isSSOExemptUser(currentUserLogin);

  // Delay PasswordExpirationGuard mount until intl is initialized with the real locale.
  // React effects fire children-before-parents, so useDmsPasswordExpiration would fire
  // before LanguageBaseContextProvider finishes fetching the user's language from the API.
  // We mount the guard only after language changes from the initial default, or after
  // a 500ms timeout (for users whose real language is also the default en-US).
  const { language } = useContext(LanguageBaseContext);
  const [langInitialized, setLangInitialized] = useState(false);
  const langTimerRef = useRef(null);

  useEffect(() => {
    if (langInitialized) return;
    // setLanguage() in LanguageBaseContextProvider fires before DmsIntlManager.init()
    // completes. A setTimeout(0) defers to the next macrotask, which is after all
    // pending microtasks (including the init await) have resolved.
    const delay = language !== 'en-US' ? 0 : 1000;
    langTimerRef.current = setTimeout(() => setLangInitialized(true), delay);
    return () => clearTimeout(langTimerRef.current);
  }, [language, langInitialized]);

  // Check institution status for read-only mode (per-institution approval)
  const {
    institutionStatuses,
    hasAtLeastOneActive,
    institutionStatus,
    institutionName,
    fetchInstitutionStatuses
  } = useInstitutionStatus();

  // TODO: Implement institution document expiration tracking
  const hasExpiredDocuments = false;
  const hasExpiringDocuments = false;
  const expiringDocNames = "";

  // Build institution alerts using the generic hook
  const institutionAlerts = useInstitutionAlerts({
    institutionStatuses,
    hasAtLeastOneActive,
    hasExpiredDocuments,
    hasExpiringDocuments,
    expiringDocNames
  });

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [canGenerateApiKey, setCanGenerateApiKey] = useState(false);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);

  // Training completion state (person-level check)
  const [trainingRequired, setTrainingRequired] = useState(false);
  const [trainingCompleted, setTrainingCompleted] = useState(true);

  // Build training alerts for PENDING_TRAINING and PENDING_RECYCLING_TRAINING statuses
  // Also checks person-level training requirement (needsTraining)
  const needsTraining = trainingRequired && !trainingCompleted;
  const trainingAlerts = useTrainingAlerts(accountStatus, needsTraining);

  const [isExternalUser, setIsExternalUser] = useState(false);
  const [isTrainingExpired, setIsTrainingExpired] = useState(false);
  const [showTrainingExpiredModal, setShowTrainingExpiredModal] = useState(false);
  const [trainingExpiredMessage, setTrainingExpiredMessage] = useState('');
  const [hasInstitutionRole, setHasInstitutionRole] = useState(false);
  const [institutionCategories, setInstitutionCategories] = useState([]);

  // Declaration status for doctors - must be checked before training
  const [userType, setUserType] = useState(null);
  const [declarationStatus, setDeclarationStatus] = useState(null);
  const [declarationChecked, setDeclarationChecked] = useState(false);

  // Build declaration alerts for doctors who haven't accepted the declaration
  // Check BOTH global declaration AND per-institution declarations
  const hasGlobalDeclaration = declarationStatus?.institution_link_declaration === true;
  const hasAllInstitutionDeclarations = declarationStatus?.institutions?.every(
    inst => inst.doctor_declaration_accepted === true
  ) ?? false;
  const hasDeclaration = hasGlobalDeclaration && hasAllInstitutionDeclarations;
  const declarationLoading = userType === 'doctor' && !declarationChecked;

  const declarationAlerts = useDeclarationAlerts(userType, hasDeclaration, declarationLoading, declarationStatus);

  // Merge all alerts
  const allAlerts = useMemo(() => {
    return [...institutionAlerts, ...trainingAlerts, ...declarationAlerts];
  }, [institutionAlerts, trainingAlerts, declarationAlerts]);

  const isPendingApproval = isExternalUser && (
    accountStatus === ACCOUNT_STATUS.PENDING_APPROVAL ||
    accountStatus === ACCOUNT_STATUS.PENDING_APPROVAL_GESTAO_LENA ||
    accountStatus === ACCOUNT_STATUS.PENDING_APPROVAL_REGULATORIO
  );
  const isPendingTraining = isExternalUser && accountStatus === ACCOUNT_STATUS.PENDING_TRAINING;
  const isPendingDocument = isExternalUser && (
    accountStatus === ACCOUNT_STATUS.PENDING_CFM_VALIDATION ||
    accountStatus === ACCOUNT_STATUS.PENDING_DOCUMENT_UPLOAD
  );
  const isRejectedOrSuspended = isExternalUser && (
    accountStatus === ACCOUNT_STATUS.REJECTED ||
    accountStatus === ACCOUNT_STATUS.SUSPENDED
  );

  // Hide stock management for institution responsible users associated with distributors
  const isDistributorResponsible = userType === 'institution_responsible' &&
    institutionCategories.includes(InstitutionCategoryEnum.Distributor);

  // Hide role-specific menus when user has a single institution that isn't approved yet
  const hasSingleUnapprovedInstitution =
    institutionStatuses.length === 1 &&
    institutionStatuses[0]?.institution_status != null &&
    institutionStatuses[0].institution_status !== InstitutionOwnStatusEnum.APPROVED;

  const hidePrescriptionMenu = hasSingleUnapprovedInstitution &&
    userType === 'doctor';

  const hideDispensationMenu = hasSingleUnapprovedInstitution &&
    userType === 'pharmacist';

  const hideMovementMenu = hasSingleUnapprovedInstitution &&
    (userType === 'institution_responsible' || userType === 'distributor_responsible' || userType === 'pharmacist');

  const fetchAccountStatus = async () => {
    try {
      const response = await fetchData("/account/status", "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        const data = response.data;
        setAccountStatus(data?.status);
        setIsExternalUser(data?.is_external_user === true);
      }
    } catch (err) {
      setIsExternalUser(false);
      setAccountStatus(null);
    }
  };

  const fetchTrainingStatus = useCallback(async () => {
    try {
      const response = await fetchData("/training_material/check-completion", "GET", true);
      const data = response?.data || response;
      setTrainingRequired(data?.requires_training || false);
      setTrainingCompleted(data?.completed || false);
    } catch (err) {
      // On error, assume training is completed to avoid blocking
      setTrainingRequired(false);
      setTrainingCompleted(true);
    }
  }, []);

  useEffect(() => {
    fetchAccountStatus();
    fetchTrainingStatus();
  }, []);

  // Fetch user type and declaration status for doctors
  // This must run before training check to establish priority
  // Also sets hasInstitutionRole for sidebar access during pending approval
  const fetchUserTypeAndDeclaration = useCallback(async () => {
    try {
      const profileResponse = await fetchData('/person/my_profile', 'GET', true);
      if (profileResponse.status === HTTP_STATUS.OK_200) {
        const profileData = profileResponse.data?.data || profileResponse.data;
        const type = profileData?.user_type || null;
        const types = profileData?.user_types || [type]; // Use new array or fallback to single type
        setUserType(type);

        // Institution roles that should get institution access during pending approval
        const institutionRoles = ['institutional_responsible', 'institution_responsible', 'distributor', 'pharmacist'];
        // Check if ANY of the user's types includes an institutional role (supports multi-type users)
        const hasRole = types.some(t => institutionRoles.includes(t));
        setHasInstitutionRole(hasRole);

        // Get institution categories for institution_responsible users
        const categories = profileData?.institution_categories || [];
        setInstitutionCategories(categories);

        // Only doctors need declaration check
        if (type === 'doctor') {
          try {
            const declResponse = await fetchData('/doctor/institution_link_declaration', 'GET', true);
            if (declResponse.status === HTTP_STATUS.OK_200) {
              setDeclarationStatus(declResponse.data?.data || declResponse.data);
            }
          } catch (declErr) {
            // Declaration check failed - don't block
          }
        }
      }
    } catch (err) {
      // Profile not accessible - may not be an external user
      setHasInstitutionRole(false);
    } finally {
      setDeclarationChecked(true);
    }
  }, []);

  useEffect(() => {
    fetchUserTypeAndDeclaration();
  }, [fetchUserTypeAndDeclaration]);

useEffect(() => {
    const handleTrainingCompleted = () => {
      fetchAccountStatus();
      // Also refresh training recycling status
      checkTrainingRecyclingStatus();
    };
    window.addEventListener('trainingCompleted', handleTrainingCompleted);
    return () => {
      window.removeEventListener('trainingCompleted', handleTrainingCompleted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleInstitutionStatusChanged = () => {
      fetchInstitutionStatuses();
    };
    window.addEventListener('institutionStatusChanged', handleInstitutionStatusChanged);
    return () => {
      window.removeEventListener('institutionStatusChanged', handleInstitutionStatusChanged);
    };
  }, [fetchInstitutionStatuses]);

  // Fetches only the declaration status — used to refresh the banner without a full profile reload
  const fetchDeclarationStatus = useCallback(async () => {
    try {
      const response = await fetchData('/doctor/institution_link_declaration', 'GET', true);
      if (response.status === HTTP_STATUS.OK_200) {
        setDeclarationStatus(response.data?.data || response.data);
      }
    } catch (err) {
      // Don't block on error
    }
  }, []);

  // Dismiss the declaration banner as soon as the doctor accepts the declaration
  useEffect(() => {
    window.addEventListener('declarationAccepted', fetchDeclarationStatus);
    return () => window.removeEventListener('declarationAccepted', fetchDeclarationStatus);
  }, [fetchDeclarationStatus]);

  // Check training RECYCLING status for controlled roles (prescriptor, dispenser, distributor, institution_responsible)
  // This popup is ONLY for users whose training certificate has EXPIRED (recycling scenario)
  // PENDING_TRAINING users are in initial training flow and handled by sidebar restrictions
  // IMPORTANT: For doctors, declaration acceptance must take precedence over training
  const checkTrainingRecyclingStatus = useCallback(async () => {
    try {
      const response = await fetchData('/training_material/check-completion?recycling=true', 'GET', true);
      const data = response.data || response;

      // Only show popup for EXPIRED certificates (recycling), not for first-time training
      // data.expired === true means the user completed training before but certificate expired
      if (data.expired === true) {
        setIsTrainingExpired(true);
        setTrainingExpiredMessage(intl.get("training_expired_message").d(
          "Your training certificate has expired. You must complete the training again before accessing the system."
        ));

        // For doctors: check if declaration is accepted first
        // If not accepted, skip training popup - declaration takes precedence
        const hasDeclaration = declarationStatus?.institution_link_declaration === true;
        if (userType === 'doctor' && !hasDeclaration) {
          return;
        }

        // Show modal if not already on training video page or profile settings
        const isTrainingVideoPage = window.location.pathname.startsWith('/training-video');
        const isProfileSettingsPage = window.location.pathname.startsWith('/profile-settings');
        const isMyTrainingsPage = window.location.pathname.startsWith('/my-trainings');
        if (!isTrainingVideoPage && !isProfileSettingsPage && !isMyTrainingsPage) {
          setShowTrainingExpiredModal(true);
        }
      } else {
        setIsTrainingExpired(false);
        setShowTrainingExpiredModal(false);
      }
    } catch (err) {
      // On error, don't block access
      setIsTrainingExpired(false);
    }
  }, [declarationStatus, userType]);

  useEffect(() => {
    const handleTrainingCompleted = () => {
      fetchAccountStatus();
      fetchTrainingStatus();
      checkTrainingRecyclingStatus();
    };
    window.addEventListener('trainingCompleted', handleTrainingCompleted);
    return () => {
      window.removeEventListener('trainingCompleted', handleTrainingCompleted);
    };
  }, [checkTrainingRecyclingStatus, fetchTrainingStatus]);

  // Check training RECYCLING only for ACTIVE users
  // PENDING_TRAINING users are in initial training flow - sidebar already restricts them
  // PENDING_RECYCLING_TRAINING users have alert banner - don't show modal (would conflict)
  // Recycling popup should only appear for users whose certificate EXPIRED
  useEffect(() => {
    if (declarationChecked && accountStatus === ACCOUNT_STATUS.ACTIVE) {
      checkTrainingRecyclingStatus();
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declarationChecked, accountStatus]);

  // Show recycling modal when navigating to non-training pages while training is expired
  // This only applies to ACTIVE users with expired certificates (recycling scenario)
  // PENDING_RECYCLING_TRAINING users are excluded - they have alert banner instead
  // Note: showTrainingExpiredModal is NOT in deps to avoid race condition on dismiss+navigate
  useEffect(() => {
    if (accountStatus !== ACCOUNT_STATUS.ACTIVE) {
      return;
    }

    const isTrainingVideoPage = location.pathname.startsWith('/training-video');
    const isProfileSettingsPage = location.pathname.startsWith('/profile-settings');
    const isMyTrainingsPage = location.pathname.startsWith('/my-trainings');

    if (isTrainingExpired && !isTrainingVideoPage && !isProfileSettingsPage && !isMyTrainingsPage) {
      const hasDeclaration = declarationStatus?.institution_link_declaration === true;
      if (userType === 'doctor' && !hasDeclaration) {
        return;
      }
      setShowTrainingExpiredModal(true);
    } else {
      setShowTrainingExpiredModal(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrainingExpired, location.pathname, userType, declarationStatus, accountStatus]);

  // Handle modal OK click - redirect to training video page
  const handleTrainingExpiredModalClose = () => {
    navigate('/training-video');
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  useEffect(() => {
    const checkApiKeyPermission = async () => {
      try {
        const response = await fetchData("/api_key/can_generate", "GET", true);
        if (response.status === HTTP_STATUS.OK_200) {
          setCanGenerateApiKey(response.data?.can_generate || false);
        }
      } catch (err) {
        setCanGenerateApiKey(false);
      }
    };
    checkApiKeyPermission();
  }, []);

  useEffect(() => {
    const getTokenPk = () => {
      try {
        const token = getToken();
        if (!token?.access_token) return null;
        const payload = JSON.parse(atob(token.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload?.pk ?? null;
      } catch {
        return null;
      }
    };

    const handleExportReady = (topic, data) => {
      const currentUserPk = getTokenPk();
      if (!currentUserPk || Number(data.requested_by_pk) !== Number(currentUserPk)) return;
      // Only add to notification bell — polling in ExportJobsProvider fires the toast
      // on every page, avoiding duplicates between SSE and polling.
      Notifications.add(`export_ready_${data.job_pk}`, {
        title: data.title || intl.get("ExportReady.Toast").d("Your export is ready for download"),
        text: data.text,
        group: "exports",
      });
    };

    const handleExportFailed = (_topic, _data) => {
      // Polling in ExportJobsProvider handles the error toast on every page.
    };

    SSE.register("SSE_EXPORT_READY", handleExportReady, "layout_export_ready");
    SSE.register("SSE_EXPORT_FAILED", handleExportFailed, "layout_export_failed");

    return () => {
      SSE.unregister("SSE_EXPORT_READY", "layout_export_ready");
      SSE.unregister("SSE_EXPORT_FAILED", "layout_export_failed");
    };
  }, []);

  const styles = theme.layout;

  const header = (
    <DmsMainHeader>
      <DmsMainHeader.Left gap="20px">
        {isMobile ? (
          <>
            <Tooltip title={intl.get("Tooltip.Action.More").d("Mais opções")}>
              <IconButton
                onClick={handleMobileDrawerToggle}
                edge="start"
                sx={{
                  color: theme.palette.neutral.black,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
                aria-label="menu"
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
            <LenaLogo showLabel={false} variant="large" />
          </>
        ) : (
          <>
            <LenaLogo showLabel={false} variant="large" />
            <DmsBarMenuButton onClick={handleClick} sxIcon={(theme) => ({ color: `${theme.palette.neutral.black} !important` })} />
          </>
        )}
      </DmsMainHeader.Left>
      <DmsMainHeader.Middle />
      <DmsMainHeader.Right>
        {canGenerateApiKey && !isSmallScreen && (
          <Tooltip title={intl.get("GenerateAPIKey").d("Generate API Key")}>
            <IconButton onClick={() => setGenerateModalOpen(true)} color="inherit" sx={{ mr: 1 }}>
              <VpnKeyIcon />
            </IconButton>
          </Tooltip>
        )}
        <DmsNotificationsMenuHeader />
        <DmsHelpCenterButton />
        <DmsUserInformation />
      </DmsMainHeader.Right>
    </DmsMainHeader>
  );

  const pendingApprovalSidebar = (
    <DmsMainSidebar.Item>
      <DmsMainSidebarOption title={intl.get("Home").d("Home")} icon={<HomeIcon />} route={ROUTES.Home} onClick={isMobile ? handleDrawerClose : undefined} />
      <DmsMainSidebarOption title={intl.get("ProfileSettings").d("Profile Settings")} icon={<SettingIcom />} route={APP_ROUTES.ProfileSettings} onClick={isMobile ? handleDrawerClose : undefined} />
    </DmsMainSidebar.Item>
  );

  const pendingApprovalWithInstitutionSidebar = (
    <DmsMainSidebar.Item>
      <DmsMainSidebarOption title={intl.get("Home").d("Home")} icon={<HomeIcon />} route={ROUTES.Home} onClick={isMobile ? handleDrawerClose : undefined} />
      <DmsMainSidebarOption title={intl.get("ProfileSettings").d("Profile Settings")} icon={<SettingIcom />} route={APP_ROUTES.ProfileSettings} onClick={isMobile ? handleDrawerClose : undefined} />

      {/* Institution access for pending approval users */}
      <DmsPermissions permissions={[Permissions.MY_INSTITUTION_PAGE]}>
        <DmsMainSidebarOption title={intl.get("MyInstitution").d("My Institution")} icon={<BusinessIcon />} route={APP_ROUTES.InstitutionCentral} onClick={isMobile ? handleDrawerClose : undefined} />
      </DmsPermissions>

      {/* Note: Training is only available after approval (status PENDING_TRAINING) */}
    </DmsMainSidebar.Item>
  );

  const pendingTrainingSidebar = (
    <DmsMainSidebar.Item>
      <DmsMainSidebarOption title={intl.get("Home").d("Home")} icon={<HomeIcon />} route={ROUTES.Home} onClick={isMobile ? handleDrawerClose : undefined} />
      <DmsMainSidebarOption title={intl.get("TrainingVideo").d("Training Video")} icon={<OndemandVideoIcon />} route={APP_ROUTES.TrainingVideo} onClick={isMobile ? handleDrawerClose : undefined} />
    </DmsMainSidebar.Item>
  );

  const pendingDocumentSidebar = (
    <DmsMainSidebar.Item>
      <DmsMainSidebarOption title={intl.get("Home").d("Home")} icon={<HomeIcon />} route={ROUTES.Home} onClick={isMobile ? handleDrawerClose : undefined} />
    </DmsMainSidebar.Item>
  );

  const institutionInactiveSidebar = (
    <DmsMainSidebar.Item>
      <DmsMainSidebarOption title={intl.get("Home").d("Home")} icon={<HomeIcon />} route={ROUTES.Home} onClick={isMobile ? handleDrawerClose : undefined} />

      {/* Institution access for users with inactive/pending institution */}
      <DmsPermissions permissions={[Permissions.MY_INSTITUTION_PAGE]}>
        <DmsMainSidebarOption title={intl.get("MyInstitution").d("My Institution")} icon={<BusinessIcon />} route={APP_ROUTES.InstitutionCentral} onClick={isMobile ? handleDrawerClose : undefined} />
      </DmsPermissions>

      {/* Training pages for all users */}
      <DmsPermissions permissions={[Permissions.TRAINING_VIDEO_PAGE]} all_needed={false}>
        <DmsMainSidebarOption title={intl.get("Training").d("Training")} icon={<OndemandVideoIcon />} route={APP_ROUTES.TrainingVideo} onClick={isMobile ? handleDrawerClose : undefined} />
      </DmsPermissions>

      <DmsPermissions permissions={[Permissions.MY_TRAININGS_PAGE]} all_needed={false}>
        <DmsMainSidebarOption title={intl.get("MyTrainings").d("My Trainings")} icon={<SchoolIcon />} route={APP_ROUTES.MyTrainings} onClick={isMobile ? handleDrawerClose : undefined} />
      </DmsPermissions>

      {/* Completed trainings - to view history */}
      <DmsPermissions permissions={[Permissions.COMPLETED_TRAININGS_PAGE]} all_needed={false}>
        <DmsMainSidebarOption title={intl.get("CompletedTrainings").d("Completed Trainings")} icon={<FactCheckIcon />} route={APP_ROUTES.CompletedTrainings} onClick={isMobile ? handleDrawerClose : undefined} />
      </DmsPermissions>

      {/* Emitted certificates - to download certificates */}
      <DmsPermissions permissions={[Permissions.EMITTED_CERTIFICATES_PAGE]} all_needed={false}>
        <DmsMainSidebarOption title={intl.get("EmittedCertificates").d("Emitted Certificates")} icon={<AssignmentIcon />} route={APP_ROUTES.EmittedCertificates} onClick={isMobile ? handleDrawerClose : undefined} />
      </DmsPermissions>
    </DmsMainSidebar.Item>
  );

  const fullSidebarContent = (
    <DmsMainSidebar.Item>
      <DmsMainSidebarOption title={intl.get("Home").d("Home")} icon={<HomeIcon />} route={ROUTES.Home} onClick={isMobile ? handleDrawerClose : undefined} />

      {/* Doctor pages - hidden when single institution is not approved */}
      {!hidePrescriptionMenu && (
        <DmsPermissions permissions={[Permissions.MY_PATIENTS_PAGE, Permissions.PRESCRIPTION_HISTORY_PAGE, Permissions.PRESCRIPTION_DISPENSATION_OVERVIEW_PAGE]} all_needed={false}>
          <DmsMainSidebarMenu title={intl.get("Prescription").d("Prescription")} icon={<AssignmentIcon />}>
            <DmsMainSidebarMenu.Item>
              <DmsPermissions permissions={Permissions.MY_PATIENTS_PAGE}>
                <DmsMainSidebarOption title={intl.get("my_patients").d("My Patients")} icon={<PeopleIcon />} route={APP_ROUTES.MyPatients} onClick={isMobile ? handleDrawerClose : undefined} />
              </DmsPermissions>
              <DmsPermissions permissions={Permissions.PRESCRIPTION_HISTORY_PAGE}>
                <DmsMainSidebarOption title={intl.get("MyPrescriptions").d("My Prescriptions")} icon={<HistoryIcon />} route={APP_ROUTES.PrescriptionHistory} onClick={isMobile ? handleDrawerClose : undefined} />
              </DmsPermissions>
              <DmsPermissions permissions={Permissions.PRESCRIPTION_DISPENSATION_OVERVIEW_PAGE}>
                <DmsMainSidebarOption title={intl.get("Reports.Menu.PrescriptionDispensation").d("Prescriptions & Dispensations")} icon={<CompareArrowsIcon />} route={APP_ROUTES.MyPrescriptionDispensation} onClick={isMobile ? handleDrawerClose : undefined} />
              </DmsPermissions>
            </DmsMainSidebarMenu.Item>
          </DmsMainSidebarMenu>
        </DmsPermissions>
      )}

      {/* Pharmacist pages - hidden when single institution is not approved */}
      {!hideDispensationMenu && (
        <DmsPermissions permissions={[Permissions.DISPENSATION_PAGE, Permissions.DISPENSATION_HISTORY_PAGE, Permissions.DISPENSATION_CANCELLATION_APPROVE]} all_needed={false}>
          <DmsMainSidebarMenu title={intl.get("Dispensation").d("Dispensation")} icon={<VaccinesIcon />}>
            <DmsMainSidebarMenu.Item>
              <DmsPermissions permissions={Permissions.DISPENSATION_PAGE}>
                <DmsMainSidebarOption title={intl.get("Dispensation").d("Dispensation")} icon={<VaccinesIcon />} route={APP_ROUTES.Dispensing} onClick={isMobile ? handleDrawerClose : undefined} />
              </DmsPermissions>

              <DmsPermissions permissions={Permissions.DISPENSATION_HISTORY_PAGE}>
                <DmsMainSidebarOption title={intl.get("DispensationHistory.Menu").d("Dispensation History")} icon={<HistoryIcon />} route={APP_ROUTES.DispensingHistory} onClick={isMobile ? handleDrawerClose : undefined} />
              </DmsPermissions>

              <DmsPermissions permissions={Permissions.DISPENSATION_CANCELLATION_APPROVE}>
                <DmsMainSidebarOption title={intl.get("CancellationRequests.Menu").d("Cancellation Requests")} icon={<CancelIcon />} route={APP_ROUTES.DispensationCancellationRequests} onClick={isMobile ? handleDrawerClose : undefined} />
              </DmsPermissions>

            </DmsMainSidebarMenu.Item>
          </DmsMainSidebarMenu>
        </DmsPermissions>
      )}

      {/* Institution menu - visible if user has any institution permission */}
      <DmsPermissions permissions={[Permissions.MY_INSTITUTION_PAGE, Permissions.INSTITUTION_PAGE, Permissions.INSTITUTION_PARTNER_GROUPS_PAGE, Permissions.INSTITUTION_MANAGEMENT_PAGE, Permissions.PARTNER_PAGE]} all_needed={false}>
        <DmsMainSidebarMenu title={intl.get("Institutions").d("Institutions")} icon={<ApartmentIcon />}>
          <DmsMainSidebarMenu.Item>
            <DmsPermissions permissions={Permissions.MY_INSTITUTION_PAGE}>
              <DmsMainSidebarOption title={intl.get("MyInstitution").d("My Institution")} icon={<BusinessIcon />} route={APP_ROUTES.InstitutionCentral} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            <DmsPermissions permissions={Permissions.INSTITUTION_APPROVE}>
              <DmsMainSidebarOption title={intl.get("InstitutionApprovals").d("Institution Approvals")} icon={<HowToReg />} route={APP_ROUTES.InstitutionApproval} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            {/* <DmsPermissions permissions={Permissions.INSTITUTION_PARTNER_GROUPS_PAGE}>
              <DmsMainSidebarOption title={intl.get("PartnerGroups").d("Partner Groups")} icon={<HandshakeIcon />} route={APP_ROUTES.InstitutionPartnerGroups} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions> */}
            <DmsPermissions permissions={Permissions.INSTITUTION_MANAGEMENT_PAGE}>
              <DmsMainSidebarOption title={intl.get("InstitutionManagement").d("Institution Management")} icon={<ApartmentIcon />} route={APP_ROUTES.InstitutionManagement} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            <DmsPermissions permissions={Permissions.PARTNER_LINK_CREATE}>
              <DmsMainSidebarOption title={intl.get("Partner").d("Partner")} icon={<HandshakeIcon />} route={APP_ROUTES.Partner} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            <DmsPermissions permissions={Permissions.BRANCH_PAGE}>
              <DmsMainSidebarOption title={intl.get("Branches").d("Branches")} icon={<HandshakeIcon />} route={APP_ROUTES.PartnerBranch} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            <DmsPermissions permissions={Permissions.PARTNER_PAGE}>
              <DmsMainSidebarOption title={intl.get("PartnersByDistributor").d("Partners by Distributor")} icon={<BusinessIcon />} route={APP_ROUTES.ReportsDistributorClients} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
          </DmsMainSidebarMenu.Item>
        </DmsMainSidebarMenu>
      </DmsPermissions>


      {/* Movement menu - Product Movement, Stock, Traceability & Returns - hidden when single institution is not approved */}
      {!hideMovementMenu && (
      <DmsPermissions permissions={[Permissions.TRACEABILITY_PAGE, Permissions.RETURN_PAGE, Permissions.SAP_INTEGRATION_PAGE, Permissions.SAP_HOMOLOGATED_INSTITUTIONS_PAGE, Permissions.RETURN_QUALITY_PAGE, Permissions.RETURN_MANAGEMENT_PAGE, Permissions.STOCK_PAGE, Permissions.STOCK_OVERVIEW_PAGE, Permissions.STOCK_MANAGEMENT_PAGE, Permissions.STOCK_RECONCILE, Permissions.STOCK_CHECKIN, Permissions.STOCK_CHECKOUT, Permissions.INSTITUTION_RESPONSIBLE_ACCESS]} all_needed={false}>
        <DmsMainSidebarMenu title={intl.get("Movement").d("Movement")} icon={<LocalShippingIcon />}>
          <DmsMainSidebarMenu.Item>
            {/* Entry (Check-in) */}
            <DmsPermissions permissions={[Permissions.STOCK_CHECKIN]}>
              <DmsMainSidebarOption title={intl.get("Checkin.PageTitle").d("Entry")} icon={<MoveToInboxIcon />} route={APP_ROUTES.Checkin} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>

            {/* Exit (Check-out) */}
            <DmsPermissions permissions={[Permissions.STOCK_CHECKOUT]}>
              <DmsMainSidebarOption title={intl.get("Checkout.PageTitle").d("Exit")} icon={<OutboxIcon />} route={APP_ROUTES.Checkout} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>

            {/* Traceability page (SAP) */}
            <DmsPermissions permissions={Permissions.SAP_INTEGRATION_PAGE}>
              <DmsMainSidebarOption title={intl.get("MovementTraceability").d("Movement Traceability")} icon={<TimelineIcon />} route={APP_ROUTES.TraceabilitySap} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>

            {/* Stock submenu - hidden for institution responsible users associated with distributors */}
            {!isDistributorResponsible && (
              <DmsPermissions permissions={[Permissions.STOCK_PAGE, Permissions.STOCK_OVERVIEW_PAGE, Permissions.STOCK_MANAGEMENT_PAGE, Permissions.STOCK_RECONCILE]} all_needed={false}>
                <DmsMainSidebarMenu title={intl.get("StockManagementManagement").d("Stock Management")} icon={<InventoryIcon />}>
                  <DmsMainSidebarMenu.Item>
                    <DmsPermissions permissions={Permissions.STOCK_OVERVIEW_PAGE}>
                      <DmsMainSidebarOption title={intl.get("StockOverview").d("Stock Overview")} icon={<InventoryIcon />} route={APP_ROUTES.InstitutionStock} onClick={isMobile ? handleDrawerClose : undefined} />
                    </DmsPermissions>
                    <DmsPermissions permissions={Permissions.STOCK_MANAGEMENT_PAGE}>
                      <DmsMainSidebarOption title={intl.get("StockManagement").d("Stock Management")} icon={<InventoryIcon />} route={APP_ROUTES.StockManagement} onClick={isMobile ? handleDrawerClose : undefined} />
                    </DmsPermissions>
                    <DmsPermissions permissions={Permissions.STOCK_RECONCILE}>
                      <DmsMainSidebarOption title={intl.get("StockReconciliation").d("Stock Reconciliation")} icon={<FactCheckIcon />} route={APP_ROUTES.StockReconciliation} onClick={isMobile ? handleDrawerClose : undefined} />
                    </DmsPermissions>
                  </DmsMainSidebarMenu.Item>
                </DmsMainSidebarMenu>
              </DmsPermissions>
            )}

            {/* Return submenu */}
            <DmsPermissions permissions={[Permissions.RETURN_PAGE, Permissions.RETURN_QUALITY_PAGE, Permissions.RETURN_MANAGEMENT_PAGE]} all_needed={false}>
              <DmsMainSidebarMenu title={intl.get("Return").d("Return")} icon={<AssignmentReturnIcon />}>
                <DmsMainSidebarMenu.Item>
                  <DmsPermissions permissions={Permissions.RETURN_PAGE}>
                    <DmsMainSidebarOption title={intl.get("Return").d("Return")} icon={<AssignmentReturnIcon />} route={APP_ROUTES.Return} onClick={isMobile ? handleDrawerClose : undefined} />
                  </DmsPermissions>
                  <DmsPermissions permissions={Permissions.RETURN_QUALITY_PAGE}>
                    <DmsMainSidebarOption title={intl.get("ReturnQualityReview").d("Return Quality Review")} icon={<VerifiedUserIcon />} route={APP_ROUTES.ReturnQuality} onClick={isMobile ? handleDrawerClose : undefined} />
                  </DmsPermissions>
                  <DmsPermissions permissions={Permissions.RETURN_MANAGEMENT_PAGE}>
                    <DmsMainSidebarOption title={intl.get("ReturnManagement").d("Return Management")} icon={<FactCheckIcon />} route={APP_ROUTES.ReturnManagement} onClick={isMobile ? handleDrawerClose : undefined} />
                  </DmsPermissions>
                </DmsMainSidebarMenu.Item>
              </DmsMainSidebarMenu>
            </DmsPermissions>

            {/* SAP Homologated Institutions */}
            <DmsPermissions permissions={Permissions.SAP_HOMOLOGATED_INSTITUTIONS_PAGE}>
              <DmsMainSidebarOption
                title={intl.get("SAPHomologatedInstitutions").d("SAP Homologated Institutions")}
                icon={<BusinessIcon />}
                route={APP_ROUTES.SapHomologatedInstitutions}
                onClick={isMobile ? handleDrawerClose : undefined}
              />
            </DmsPermissions>


          </DmsMainSidebarMenu.Item>
        </DmsMainSidebarMenu>
      </DmsPermissions>
      )}

      {/* Reports and Traceability pages with submenu */}
      <DmsPermissions permissions={Permissions.REPORTS_PAGE}>
        <DmsMainSidebarMenu title={intl.get("ReportsTraceability").d("Reports and Traceability")} icon={<AutoGraphIcon />}>
          <DmsMainSidebarMenu.Item>
            {/* Sales report - requires REPORTS_SALES or legacy REPORTS_PAGE without granular permissions */}
            <DmsPermissions permissions={[Permissions.REPORTS_SALES, Permissions.REPORTS_DETAILED, Permissions.REPORTS_EXECUTIVE]} all_needed={false}>
              <DmsMainSidebarOption title={intl.get("Analytics.Menu.Sales").d("Sales")} icon={<TrendingUpIcon />} route={APP_ROUTES.AnalyticsSales} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            {/* Traceability report */}
            <DmsPermissions permissions={[Permissions.REPORTS_TRACEABILITY, Permissions.REPORTS_DETAILED, Permissions.REPORTS_EXECUTIVE]} all_needed={false}>
              <DmsMainSidebarOption title={intl.get("Analytics.Menu.Traceability").d("Traceability")} icon={<TimelineIcon />} route={APP_ROUTES.AnalyticsTraceability} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            {/* Direct Channel Clients report */}
            <DmsPermissions permissions={[Permissions.REPORTS_QUALIFIED_CLIENTS, Permissions.REPORTS_EXECUTIVE]} all_needed={false}>
              <DmsMainSidebarOption title={intl.get("Reports.Menu.DirectChannelClients").d("Direct Channel Clients Report")} icon={<VerifiedUserIcon />} route={APP_ROUTES.ReportsDirectChannelClients} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            {/* Prescriptions & Dispensations overview */}
            <DmsPermissions permissions={[Permissions.REPORTS_PRESCRIPTIONS_DISPENSATIONS, Permissions.REPORTS_EXECUTIVE]} all_needed={false}>
              <DmsMainSidebarOption title={intl.get("Reports.Menu.PrescriptionDispensation").d("Prescriptions & Dispensations")} icon={<CompareArrowsIcon />} route={APP_ROUTES.ReportsPrescriptionDispensation} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            {/* Combined Prescriptions and Dispensations report */}
            <DmsPermissions permissions={[Permissions.REPORTS_PRESCRIPTIONS_DISPENSATIONS_COMBINED, Permissions.REPORTS_EXECUTIVE]} all_needed={false}>
              <DmsMainSidebarOption title={intl.get("Reports.Menu.PrescriptionsDispensations").d("Prescriptions and Dispensations")} icon={<AssignmentIcon />} route={APP_ROUTES.ReportsPrescriptionsDispensations} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            {/* Institutions report */}
            <DmsPermissions permissions={[Permissions.REPORTS_INSTITUTIONS, Permissions.REPORTS_DETAILED]} all_needed={false}>
              <DmsMainSidebarOption title={intl.get("Reports.Menu.Institutions").d("Institutions")} icon={<ApartmentIcon />} route={APP_ROUTES.ReportsInstitutions} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            {/* Dispensations report */}
            <DmsPermissions permissions={[Permissions.REPORTS_DISPENSATIONS, Permissions.REPORTS_DETAILED]} all_needed={false}>
              <DmsMainSidebarOption title={intl.get("Reports.Menu.Dispensation").d("Dispensation")} icon={<VaccinesIcon />} route={APP_ROUTES.ReportsDispensations} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            {/* Prescriptions report */}
            <DmsPermissions permissions={[Permissions.REPORTS_PRESCRIPTIONS, Permissions.REPORTS_DETAILED]} all_needed={false}>
              <DmsMainSidebarOption title={intl.get("Reports.Menu.Prescription").d("Prescription Report")} icon={<HistoryIcon />} route={APP_ROUTES.ReportsPrescriptions} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            {/* Pharmacists/Dispensers report */}
            <DmsPermissions permissions={[Permissions.REPORTS_PHARMACISTS, Permissions.REPORTS_DETAILED]} all_needed={false}>
              <DmsMainSidebarOption title={intl.get("Reports.Menu.Pharmacist").d("Dispenser Report")} icon={<LocalPharmacyIcon />} route={APP_ROUTES.ReportsPharmacists} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            {/* Patients report */}
            <DmsPermissions permissions={[Permissions.REPORTS_PATIENTS, Permissions.REPORTS_DETAILED]} all_needed={false}>
              <DmsMainSidebarOption title={intl.get("Reports.Menu.Patients").d("Patients")} icon={<PeopleIcon />} route={APP_ROUTES.ReportsPatients} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
            {/* Doctors/Prescribers report */}
            <DmsPermissions permissions={[Permissions.REPORTS_DOCTORS, Permissions.REPORTS_DETAILED]} all_needed={false}>
              <DmsMainSidebarOption title={intl.get("Reports.Menu.Doctors").d("Prescribers Report")} icon={<MedicalServicesIcon />} route={APP_ROUTES.ReportsDoctors} onClick={isMobile ? handleDrawerClose : undefined} />
            </DmsPermissions>
          </DmsMainSidebarMenu.Item>
        </DmsMainSidebarMenu>
      </DmsPermissions>

      <DmsPermissions permissions={[Permissions.SYSTEM_SETTINGS_PAGE, Permissions.EUROFARMA_GESTAO_LENA, Permissions.TRAINING_VIDEO_PAGE, Permissions.CAN_MANAGE_API_KEYS, Permissions.PENDING_APPROVALS_PAGE, Permissions.NOTICE_PAGE]} all_needed={false}>
        <DmsMainSidebarMenu title={intl.get("System").d("System")} icon={<SystemIcon />}>
          <DmsPermissions permissions={Permissions.ADVANCED_AUDIT_PAGE}>
            <DmsMainSidebarOption title={intl.get("AuditTrail").d("Audit Trail")} icon={<Security />} route={APP_ROUTES.AdvancedAudit} onClick={isMobile ? handleDrawerClose : undefined} />
          </DmsPermissions>
          <DmsMainSidebarOption title={intl.get("MyDownloads").d("My Downloads")} icon={<DownloadIcon />} route={APP_ROUTES.MyDownloads} onClick={isMobile ? handleDrawerClose : undefined} />
          <DmsPermissions permissions={Permissions.SYSTEM_SETTINGS_PAGE}>
            <DmsMainSidebarOption title={intl.get("ProductionData").d("Production Data")} icon={<ProductsIcom />} route={ROUTES.Product} onClick={isMobile ? handleDrawerClose : undefined} />
            {/* <DmsMainSidebarOption title={intl.get("PrescriptorSettings").d("Prescriptor Settings")} icon={<ListAltIcon />} route={APP_ROUTES.PrescriptorSettings} onClick={isMobile ? handleDrawerClose : undefined} /> */}
          </DmsPermissions>
          <DmsPermissions permissions={[Permissions.SYSTEM_SETTINGS_PAGE, Permissions.CAN_MANAGE_GENERAL_SETTINGS, Permissions.CAN_MANAGE_SAP_SETTINGS, Permissions.CAN_MANAGE_API_KEYS, Permissions.CAN_MANAGE_SMTP_SETTINGS, Permissions.CAN_MANAGE_SSO_SETTINGS, Permissions.CAN_MANAGE_LOGIN_SETTINGS, Permissions.CAN_MANAGE_SECURITY_SETTINGS]} all_needed={false}>
            <DmsMainSidebarOption title={intl.get("Settings").d("Settings")} icon={<SettingIcom />} route={ROUTES.SystemSettings} onClick={isMobile ? handleDrawerClose : undefined} />
          </DmsPermissions>
          <DmsPermissions permissions={[Permissions.CAN_MANAGE_HELP_CENTER, Permissions.CAN_MANAGE_GENERAL_SETTINGS]} all_needed={false}>
            <DmsMainSidebarOption title={intl.get("Content").d("Content")} icon={<ArticleIcon />} route={APP_ROUTES.ContentSettings} onClick={isMobile ? handleDrawerClose : undefined} />
          </DmsPermissions>
          {/* Admin pages - Users menu visible if user has any of the submenu permissions */}
          <DmsPermissions permissions={[Permissions.USER_MANAGEMENT_PAGE, Permissions.PENDING_APPROVALS_PAGE, Permissions.ROLES_PAGE, Permissions.DIRECTOR_GROUPS_PAGE]} all_needed={false}>
            <DmsMainSidebarMenu title={intl.get("UserManagementManagement").d("User Management")} icon={<UserIcon />}>
              <DmsMainSidebarMenu.Item>
                <DmsPermissions permissions={Permissions.PENDING_APPROVALS_PAGE}>
                  <DmsMainSidebarOption title={intl.get("PendingApprovals").d("Pending Approvals")} icon={<HowToReg />} route={APP_ROUTES.PendingAccountApprovals} onClick={isMobile ? handleDrawerClose : undefined} />
                </DmsPermissions>
                <DmsPermissions permissions={Permissions.USER_MANAGEMENT_PAGE}>
                  <DmsMainSidebarOption title={intl.get("UserManagement").d("User Management")} icon={<UserIcon />} route={APP_ROUTES.UserManagement} onClick={isMobile ? handleDrawerClose : undefined} />
                </DmsPermissions>
                <DmsPermissions permissions={Permissions.ROLES_PAGE}>
                  <DmsMainSidebarOption title={intl.get("Roles").d("Roles")} icon={<RoleIcon />} route={ROUTES.Role} onClick={isMobile ? handleDrawerClose : undefined} />
                </DmsPermissions>
                <DmsPermissions permissions={Permissions.DIRECTOR_GROUPS_PAGE}>
                  <DmsMainSidebarOption title={intl.get("DirectorGroups").d("Director Groups")} icon={<GroupsIcon />} route={APP_ROUTES.DirectorGroups} onClick={isMobile ? handleDrawerClose : undefined} />
                </DmsPermissions>
              </DmsMainSidebarMenu.Item>
            </DmsMainSidebarMenu>
          </DmsPermissions>

          <DmsPermissions permissions={Permissions.ALERT_SYSTEM_PAGE}>
            <DmsMainSidebarMenu title={intl.get("AlertSystem").d("Alert System")} icon={<AlarmIcon />}>
              <DmsMainSidebarMenu.Item>
                <DmsMainSidebarOption title={intl.get("ManageAlerts").d("Manage Alerts")} icon={<AddAlertIcon />} route={ALERT_ROUTES.DmsAlertPage} onClick={isMobile ? handleDrawerClose : undefined} />
                {/* <DmsMainSidebarOption title={intl.get("ActiveAlerts").d("Active Alerts")} icon={<NotificationsActiveIcon />} route={ALERT_ROUTES.DmsActiveAlertPage} onClick={isMobile ? handleDrawerClose : undefined} /> */}
              </DmsMainSidebarMenu.Item>
            </DmsMainSidebarMenu>
          </DmsPermissions>

          <DmsPermissions permissions={Permissions.NOTICE_PAGE}>
            <DmsMainSidebarOption title={intl.get("SystemNotices").d("Notices")} icon={<WarningAmberIcon />} route={APP_ROUTES.SystemNotice} onClick={isMobile ? handleDrawerClose : undefined} />
          </DmsPermissions>

          {/* Import pages hidden from sidebar - access via direct URL only (DMS_ACCENTURE) */}

          <DmsPermissions permissions={[Permissions.TRAINING_VIDEO_PAGE]} all_needed={false}>
            <DmsMainSidebarMenu title={intl.get("TrainingCentral").d("Training Central")} icon={<SchoolIcon />}>
              <DmsMainSidebarMenu.Item>
                <DmsPermissions permissions={Permissions.TRAINING_CENTRAL_PAGE}>
                  <DmsMainSidebarOption title={intl.get("TrainingCentral").d("Training Central")} icon={<SchoolIcon />} route={APP_ROUTES.TrainingCentral} onClick={isMobile ? handleDrawerClose : undefined} />
                </DmsPermissions>
                {/* Registro de Treinamento - For all authenticated users */}
                <DmsMainSidebarOption title={intl.get("TrainingReports").d("Training Reports")} icon={<SchoolIcon />} route={APP_ROUTES.MyTrainings} onClick={isMobile ? handleDrawerClose : undefined} />
                <DmsPermissions permissions={[Permissions.COMPLETED_TRAININGS_PAGE]} all_needed={false}>
                  <DmsMainSidebarOption title={intl.get("CompletedTrainings").d("Completed Trainings")} icon={<FactCheckIcon />} route={APP_ROUTES.CompletedTrainings} onClick={isMobile ? handleDrawerClose : undefined} />
                </DmsPermissions>
              </DmsMainSidebarMenu.Item>
            </DmsMainSidebarMenu>
          </DmsPermissions>
        </DmsMainSidebarMenu>
      </DmsPermissions>

      {/* Training video for external users */}
      <DmsPermissions permissions={[Permissions.TRAINING_VIDEO_PAGE]} all_needed={false}>
        <DmsMainSidebarOption title={intl.get("Training").d("Training")} icon={<OndemandVideoIcon />} route={APP_ROUTES.TrainingVideo} onClick={isMobile ? handleDrawerClose : undefined} />
      </DmsPermissions>
    </DmsMainSidebar.Item>
  );

  const sidebarContent = isRejectedOrSuspended
    ? pendingDocumentSidebar
    : isPendingApproval
      ? (hasInstitutionRole ? pendingApprovalWithInstitutionSidebar : pendingApprovalSidebar)
      : isPendingDocument
        ? pendingDocumentSidebar
        : isPendingTraining
          ? pendingTrainingSidebar
          : isTrainingExpired
            ? pendingTrainingSidebar
            : !hasAtLeastOneActive
              ? institutionInactiveSidebar
              : fullSidebarContent;

  const sidebar = (
    <DmsMainSidebar collapsed={collapsed}>
      {sidebarContent}
    </DmsMainSidebar>
  );

  const mobileDrawer = (
    <Drawer
      variant="temporary"
      open={mobileDrawerOpen}
      onClose={handleDrawerClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: 'block', md: 'none' },
        '& .MuiDrawer-paper': { width: 250, boxSizing: 'border-box' },
      }}
    >
      <DmsMainSidebar collapsed={false}>
        {sidebarContent}
      </DmsMainSidebar>
    </Drawer>
  );

  const sidebarStyle = () => {
    if (isMobile) return { display: 'none' };
    if (collapsed && isSmallScreen) return styles.sidebarCollapsedMobile;
    if (collapsed && !isSmallScreen) return styles.sidebarCollapsed;
    return styles.sidebar;
  }

  return (
    <ExportJobsProvider>
    <Box sx={styles.root}>
      <Box sx={styles.header}>{header}</Box>

      <Box sx={styles.contentWrapper}>
        <Box sx={sidebarStyle()}>{sidebar}</Box>
        {mobileDrawer}
        <Box component="main" sx={styles.mainContent}>
          {/* System Alerts Banner - positioned inside main content area */}
          <AlertBanner alerts={allAlerts} />

          {/* DocumentUploadBanner - Shows when institution status is PENDING_DOCUMENT_UPLOAD */}
          <DocumentUploadBanner
            isVisible={institutionStatus === InstitutionStatusEnum.PENDING_DOCUMENT_UPLOAD && !hasExpiredDocuments && !hasExpiringDocuments}
            institutionName={institutionName}
          />

          <Outlet />
        </Box>
      </Box>
      <ToastContainer />
      <GenerateApiKeyModal
        open={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        onSuccess={() => {
          setCanGenerateApiKey(false);
          setGenerateModalOpen(false);
        }}
      />

      {/* Password expiration warning — skipped for SSO/external users */}
      {!ssoExempt && langInitialized && <PasswordExpirationGuard />}

      {/* Training Expired Modal for Distributors */}
      <TrainingModal
        isVisible={showTrainingExpiredModal}
        onClose={handleTrainingExpiredModalClose}
        title={intl.get("training_expired_title").d("Training Required")}
        message={trainingExpiredMessage}
        instructionText={intl.get("training_expired_instruction").d(
          "Please complete the required training video to continue using the system."
        )}
      />
    </Box>
    </ExportJobsProvider>
  );
};
